// useResizablePanel — docked panel resize via pointer-event drag (plan
// 2026-07-29, option A: the panel stays anchored to the right/bottom).
//
// Drag the LEFT edge to change width, the TOP edge to change height, or the
// TOP-LEFT corner for both. Height `null` means "full viewport height" (the
// default); once the user drags the top edge it becomes a concrete pixel value
// and the panel docks bottom-right.
//
// The clamp and persistence helpers are pure and exported so the logic is unit-
// tested without a DOM. Sizes are re-clamped on window resize so a value saved
// on a large screen can never overflow a smaller one.
import { useCallback, useEffect, useRef, useState } from "react";

export type ResizeAxis = "x" | "y" | "both";

export interface PanelSize {
  /** Panel width in px. */
  width: number;
  /** Panel height in px, or `null` for full viewport height. */
  height: number | null;
}

export const STORAGE_KEY = "verity.assistant.panelSize";

export const DEFAULT_WIDTH = 480;
export const MIN_WIDTH = 360;
export const MIN_HEIGHT = 420;

/** Default docked size: 480px wide, full viewport height. */
export const DEFAULT_SIZE: PanelSize = { width: DEFAULT_WIDTH, height: null };

/** Clamp width to [MIN_WIDTH, min(900, 90% of viewport width)]. */
export function clampWidth(width: number, viewportWidth: number): number {
  const max = Math.max(MIN_WIDTH, Math.min(900, Math.round(viewportWidth * 0.9)));
  return Math.min(Math.max(Math.round(width), MIN_WIDTH), max);
}

/**
 * Clamp height to [MIN_HEIGHT, viewport height]. `null` (full height) passes
 * through unchanged.
 */
export function clampHeight(
  height: number | null,
  viewportHeight: number,
): number | null {
  if (height == null) return null;
  const max = Math.max(MIN_HEIGHT, viewportHeight);
  return Math.min(Math.max(Math.round(height), MIN_HEIGHT), max);
}

/** Clamp both axes of a PanelSize against the given viewport. */
export function clampSize(
  size: PanelSize,
  viewportWidth: number,
  viewportHeight: number,
): PanelSize {
  return {
    width: clampWidth(size.width, viewportWidth),
    height: clampHeight(size.height, viewportHeight),
  };
}

/**
 * Read the persisted size from localStorage. Corrupt, missing, or partially
 * invalid values fall back to the defaults (480 × full height) per field.
 */
export function loadPanelSize(): PanelSize {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SIZE };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { ...DEFAULT_SIZE };
    const record = parsed as Record<string, unknown>;
    const width =
      typeof record.width === "number" && Number.isFinite(record.width)
        ? record.width
        : DEFAULT_WIDTH;
    const height =
      record.height == null
        ? null
        : typeof record.height === "number" && Number.isFinite(record.height)
          ? record.height
          : null;
    return { width, height };
  } catch {
    return { ...DEFAULT_SIZE };
  }
}

/** Persist the size; swallows quota/private-mode errors. */
export function savePanelSize(size: PanelSize): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(size));
  } catch {
    /* localStorage unavailable (quota, private mode) — non-fatal. */
  }
}

interface DragState {
  axis: ResizeAxis;
  pointerId: number;
  startX: number;
  startY: number;
  startWidth: number;
  /** Effective start height in px (viewport height when height was null). */
  startHeight: number;
}

export interface HandleProps {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onDoubleClick: () => void;
  style: React.CSSProperties;
}

export interface UseResizablePanel {
  /** Current clamped size. */
  size: PanelSize;
  /** Reset one axis to its default (width → 480, height → full). */
  resetAxis: (axis: "x" | "y") => void;
  /** Props to spread onto a resize handle for the given axis. */
  getHandleProps: (axis: ResizeAxis) => HandleProps;
}

function readViewport(): { w: number; h: number } {
  return { w: window.innerWidth, h: window.innerHeight };
}

/**
 * Manage a docked, user-resizable panel size with pointer-event drag,
 * localStorage persistence, viewport re-clamping, and double-click reset.
 */
export function useResizablePanel(): UseResizablePanel {
  const [size, setSize] = useState<PanelSize>(() => {
    const { w, h } = readViewport();
    return clampSize(loadPanelSize(), w, h);
  });

  const dragRef = useRef<DragState | null>(null);

  // Persist whenever the size settles/changes.
  useEffect(() => {
    savePanelSize(size);
  }, [size]);

  // Re-clamp on viewport resize so a saved large size can't overflow.
  useEffect(() => {
    const onResize = () => {
      const { w, h } = readViewport();
      setSize((prev) => clampSize(prev, w, h));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Restore body text-selection on unmount if a drag was mid-flight.
  useEffect(() => {
    return () => {
      document.body.style.userSelect = "";
    };
  }, []);

  const resetAxis = useCallback((axis: "x" | "y") => {
    setSize((prev) => ({
      width: axis === "x" ? DEFAULT_WIDTH : prev.width,
      height: axis === "y" ? null : prev.height,
    }));
  }, []);

  const onPointerDown = useCallback(
    (axis: ResizeAxis) => (e: React.PointerEvent) => {
      // Ignore secondary buttons (context menu, etc.).
      if (e.button !== 0 && e.pointerType === "mouse") return;
      e.preventDefault();
      e.stopPropagation();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* setPointerCapture can throw if the pointer is already gone. */
      }
      dragRef.current = {
        axis,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width,
        startHeight: size.height ?? window.innerHeight,
      };
      document.body.style.userSelect = "none";
    },
    [size.width, size.height],
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    e.preventDefault();
    const { w, h } = readViewport();
    setSize((prev) => {
      const next: PanelSize = { ...prev };
      // Panel is anchored right: dragging the left edge left grows width.
      if (drag.axis === "x" || drag.axis === "both") {
        next.width = clampWidth(drag.startWidth - (e.clientX - drag.startX), w);
      }
      // Panel is anchored bottom: dragging the top edge up grows height.
      if (drag.axis === "y" || drag.axis === "both") {
        next.height = clampHeight(drag.startHeight - (e.clientY - drag.startY), h);
      }
      return next;
    });
  }, []);

  const endDrag = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    dragRef.current = null;
    document.body.style.userSelect = "";
  }, []);

  const getHandleProps = useCallback(
    (axis: ResizeAxis): HandleProps => ({
      onPointerDown: onPointerDown(axis),
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick: () => {
        if (axis === "x" || axis === "both") resetAxis("x");
        if (axis === "y" || axis === "both") resetAxis("y");
      },
      style: { touchAction: "none" },
    }),
    [onPointerDown, onPointerMove, endDrag, resetAxis],
  );

  return { size, resetAxis, getHandleProps };
}
